import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FiBell, FiCheck, FiTrash2, FiX, FiUsers, FiFolder, FiCheckSquare, FiUserPlus, FiShield, FiLayers } from 'react-icons/fi';
import { toast } from 'sonner';
import {
  getOrgNotifications,
  getOrgUnreadCount,
  markOrgNotificationAsRead,
  markAllOrgNotificationsAsRead,
  deleteOrgNotification
} from '../../services/orgNotifications.service';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../context/OrganizationContext';

// Hide scrollbar CSS
const scrollbarHideStyle = `
  .notification-scroll::-webkit-scrollbar {
    display: none;
  }
`;

function getChatNotificationUrl(notification) {
  const conversationId = notification?.metadata?.conversationId;
  if (!conversationId) return null;
  const isChatNotification =
    notification.type === 'chat_message' ||
    notification.type === 'chat_mention' ||
    notification.actionUrl?.includes('/messages');
  if (!isChatNotification) return null;
  return `/org/messages?conversationId=${conversationId}`;
}

// Get notification icon based on type
const getNotificationIcon = (type) => {
  const iconClass = "h-4 w-4";
  switch (type) {
    case 'org_invitation_sent':
    case 'org_invitation_accepted':
    case 'org_user_joined':
      return <FiUserPlus className={`${iconClass} text-green-500`} />;
    case 'org_user_role_changed':
      return <FiShield className={`${iconClass} text-purple-500`} />;
    case 'org_team_created':
    case 'org_team_updated':
    case 'org_team_deleted':
    case 'org_team_member_added':
      return <FiUsers className={`${iconClass} text-blue-500`} />;
    case 'org_department_created':
    case 'org_department_updated':
      return <FiLayers className={`${iconClass} text-orange-500`} />;
    case 'org_project_created':
    case 'org_project_updated':
      return <FiFolder className={`${iconClass} text-indigo-500`} />;
    case 'task_assigned':
    case 'task_updated':
    case 'task_completed':
    case 'task_comment':
      return <FiCheckSquare className={`${iconClass} text-teal-500`} />;
    default:
      return <FiBell className={`${iconClass} text-gray-500`} />;
  }
};

const OrgNotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const portalRef = useRef(null);
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();

  useEffect(() => {
    if (currentOrganization) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (isOpen && currentOrganization) fetchNotifications();
  }, [isOpen, currentOrganization]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideButton = dropdownRef.current && !dropdownRef.current.contains(e.target);
      const isOutsidePortal = !portalRef.current || !portalRef.current.contains(e.target);
      if (isOutsideButton && isOutsidePortal) setIsOpen(false);
    };
    const handleEscape = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (!isOpen) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const fetchUnreadCount = async () => {
    try {
      const response = await getOrgUnreadCount();
      if (response.success) setUnreadCount(response.data.unread);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getOrgNotifications({ limit: 20 });
      if (response.success) setNotifications(response.data);
    } catch (error) {
      if (!error.response?.data?.error?.includes('Organization context')) {
        toast.error('Failed to load notifications');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await markOrgNotificationAsRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch { toast.error('Failed to mark as read'); }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    try {
      await markAllOrgNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { toast.error('Failed to mark all as read'); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteOrgNotification(id);
      const deleted = notifications.find(n => n._id === id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      if (!deleted?.isRead) setUnreadCount(c => Math.max(0, c - 1));
    } catch { toast.error('Failed to delete'); }
  };

  const handleClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await markOrgNotificationAsRead(notification._id);
        setUnreadCount(c => Math.max(0, c - 1));
        setNotifications(prev => prev.map(n => n._id === notification._id ? { ...n, isRead: true } : n));
      } catch {}
    }
    const chatUrl = getChatNotificationUrl(notification);
    if (chatUrl || notification.actionUrl) {
      navigate(chatUrl || notification.actionUrl);
      setIsOpen(false);
    }
  };

  const formatTime = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!currentOrganization) return null;

  const dropdownContent = isOpen && ReactDOM.createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998]"
        onClick={() => setIsOpen(false)}
        aria-hidden
      />
      <div
        ref={portalRef}
        className="fixed top-14 right-4 z-[9999] w-96 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
      >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button 
                  onClick={handleMarkAllAsRead} 
                  className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 rounded-md transition-colors"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                <FiX className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="notification-scroll max-h-[420px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-14 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <FiBell className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No notifications yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">We'll notify you when something arrives</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n._id}
                  onClick={() => handleClick(n)}
                  className={`group flex gap-2.5 px-3 py-2.5 cursor-pointer transition-colors
                    hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!n.isRead ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
                >
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${!n.isRead ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-gray-100 dark:bg-gray-700/50'}`}>
                    {getNotificationIcon(n.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1.5">
                      <p className={`text-[13px] leading-tight ${!n.isRead ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300 font-medium'}`}>
                        {n.title}
                      </p>
                      {!n.isRead && (
                        <span className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 bg-blue-500 rounded-full" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 leading-snug line-clamp-2">
                      {n.message}
                    </p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-gray-400 dark:text-gray-500">{formatTime(n.createdAt)}</span>
                      {/* Hover actions */}
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        {!n.isRead && (
                          <button 
                            onClick={(e) => handleMarkAsRead(n._id, e)} 
                            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors" 
                            title="Mark as read"
                          >
                            <FiCheck className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                          </button>
                        )}
                        <button 
                          onClick={(e) => handleDelete(n._id, e)} 
                          className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors" 
                          title="Delete"
                        >
                          <FiTrash2 className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <button
                onClick={() => { navigate('/org/notifications'); setIsOpen(false); }}
                className="w-full py-3 text-sm font-medium text-center text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
    </>,
    document.body
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <style>{scrollbarHideStyle}</style>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-full transition-colors ${isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
        style={{ color: 'var(--header-text-muted, #6B7280)' }}
        aria-label="Notifications"
      >
        <FiBell className="h-5 w-5" style={{ color: 'inherit' }} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>

      {dropdownContent}
    </div>
  );
};

export default OrgNotificationBell;
